import { Button } from 'antd'
import React, { useEffect, useState } from 'react'
import {
  getCaptchaOptions,
  getManageSettingList,
  setManageSetting,
} from '../../api/manage.api'

const ManageOnOffPage = () => {
  const [settings, setSettings] = useState([])
  const [loading, setLoading] = useState(true)
  const [editedSettings, setEditedSettings] = useState({})
  const [saveMessage, setSaveMessage] = useState(null)
  const [captchaOptions, setCaptchaOptions] = useState([])

  const fetchCaptchaOptions = async () => {
    try {
      const res = await getCaptchaOptions()
      const data = res?.data
      // data can be either ["ANTI_CAPTCHA", ...] or [{ code, name }]
      const normalized = Array.isArray(data)
        ? typeof data[0] === 'string'
          ? data.map((code) => ({ code, name: code }))
          : data
        : []
      setCaptchaOptions(normalized)
    } catch (e) {
      console.error('캡챠 옵션 조회 실패', e)
      setCaptchaOptions([])
    }
  }

  const fetchSettings = async () => {
    try {
      const res = await getManageSettingList({})
      setSettings(res.data.content)
      const initialEdited = {}
      res.data.content.forEach((setting) => {
        initialEdited[setting.id] = {
          userCountPerBatch: setting.userCountPerBatch,
          sleepIntervalPerUser: setting.sleepIntervalPerUser,
          isOn: setting.isOn,
          captchaSolution: setting.captchaSolution ?? '',
        }
      })
      setEditedSettings(initialEdited)
    } catch (e) {
      console.error('세팅 목록 조회 실패', e)
    } finally {
      setLoading(false)
    }
  }

  const toggleSetting = (id, currentValue) => {
    const original = settings.find((s) => s.id === id) || {}
    setEditedSettings((prev) => ({
      ...prev,
      [id]: {
        userCountPerBatch:
          prev[id]?.userCountPerBatch ?? original.userCountPerBatch,
        sleepIntervalPerUser:
          prev[id]?.sleepIntervalPerUser ?? original.sleepIntervalPerUser,
        isOn: !currentValue,
        captchaSolution: prev[id]?.captchaSolution ?? original.captchaSolution,
      },
    }))
  }

  const handleFieldChange = (id, field, value) => {
    const original = settings.find((s) => s.id === id) || {}
    setEditedSettings((prev) => ({
      ...prev,
      [id]: {
        userCountPerBatch:
          prev[id]?.userCountPerBatch ?? original.userCountPerBatch,
        sleepIntervalPerUser:
          prev[id]?.sleepIntervalPerUser ?? original.sleepIntervalPerUser,
        isOn: prev[id]?.isOn ?? original.isOn,
        captchaSolution: prev[id]?.captchaSolution ?? original.captchaSolution,
        [field]: value,
      },
    }))
  }

  const handleSaveChanges = async (id) => {
    try {
      const original = settings.find((s) => s.id === id)
      const values = editedSettings[id] ?? {}
      const payload = {
        settingId: Number(id),
        userCountPerBatch:
          values.userCountPerBatch ?? original.userCountPerBatch,
        sleepIntervalPerUser:
          values.sleepIntervalPerUser ?? original.sleepIntervalPerUser,
        isOn: values.isOn ?? original.isOn,
        captchaSolution: values.captchaSolution ?? original.captchaSolution,
      }
      await setManageSetting(payload)
      await fetchSettings()
      setSaveMessage('저장 완료되었습니다.')
    } catch (e) {
      console.error('변경사항 저장 실패', e)
      setSaveMessage('저장 실패: 오류가 발생했습니다.')
    }
  }

  useEffect(() => {
    const load = async () => {
      await fetchCaptchaOptions()
      await fetchSettings()
    }
    load()
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (saveMessage) {
      const timeout = setTimeout(() => setSaveMessage(null), 3000)
      return () => clearTimeout(timeout)
    }
  }, [saveMessage])

  if (loading) {
    return <p>로딩 중...</p>
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    const yyyy = date.getFullYear()
    const mm = String(date.getMonth() + 1).padStart(2, '0')
    const dd = String(date.getDate()).padStart(2, '0')
    const hh = String(date.getHours()).padStart(2, '0')
    const min = String(date.getMinutes()).padStart(2, '0')
    const ss = String(date.getSeconds()).padStart(2, '0')
    return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`
  }

  return (
    <div className="container">
      <h2 style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        설정 목록
        <span style={{ fontSize: '0.95rem', color: 'red', fontWeight: 'bold' }}>
          ※ 변경 사항 발생 시 저장 필수 (활성화 여부 포함)
        </span>
      </h2>
      <Button
        type="default"
        href="https://www.notion.so/257203bac0ea806c8fdef9feb933509f?source=copy_link"
        target="_blank"
        rel="noopener noreferrer"
        style={{ marginBottom: '16px', float: 'right' }}
      >
        📢 동기화 대응 매뉴얼
      </Button>
      <table>
        <thead>
          <tr>
            <th>ID</th>
            <th>설정 이름</th>
            <th>활성화 여부</th>
            <th>캡챠 솔루션</th>
            <th></th>
            <th>생성일</th>
            <th>수정일</th>
          </tr>
        </thead>
        <tbody>
          {settings.map((setting) => (
            <tr key={setting.id}>
              <td>{setting.id}</td>
              <td>{setting.setting_name}</td>
              <td>
                <div
                  className={`toggle-switch ${
                    (editedSettings[setting.id]?.isOn ?? setting.isOn)
                      ? 'on'
                      : 'off'
                  }`}
                  onClick={() =>
                    toggleSetting(
                      setting.id,
                      editedSettings[setting.id]?.isOn ?? setting.isOn,
                    )
                  }
                >
                  <span className="toggle-thumb">
                    {(editedSettings[setting.id]?.isOn ?? setting.isOn)
                      ? 'ON'
                      : 'OFF'}
                  </span>
                </div>
              </td>
              <td>
                <select
                  value={
                    editedSettings[setting.id]?.captchaSolution ??
                    setting.captchaSolution ??
                    ''
                  }
                  onChange={(e) =>
                    handleFieldChange(
                      setting.id,
                      'captchaSolution',
                      e.target.value,
                    )
                  }
                  style={{ minWidth: '160px' }}
                >
                  <option value="" disabled>
                    선택하세요
                  </option>
                  {captchaOptions.map((opt) => {
                    const label =
                      opt.name && opt.name !== opt.code
                        ? `${opt.name} (${opt.code})`
                        : opt.code
                    return (
                      <option key={opt.code} value={opt.code}>
                        {label}
                      </option>
                    )
                  })}
                </select>
              </td>
              <td>
                <button
                  onClick={() => handleSaveChanges(setting.id)}
                  disabled={false}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: '#4caf50',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                >
                  저장
                </button>
              </td>
              <td>{formatDate(setting.createdAt)}</td>
              <td className="td-date">{formatDate(setting.updatedAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {saveMessage && (
        <div
          style={{
            position: 'fixed',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: saveMessage.includes('실패')
              ? '#f44336'
              : '#4caf50',
            color: '#fff',
            padding: '12px 20px',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            zIndex: 9999,
            transition: 'opacity 0.3s ease-in-out',
          }}
        >
          {saveMessage}
        </div>
      )}
      <style>{styles}</style>
    </div>
  )
}

export default ManageOnOffPage

// Add this outside of the return block
const styles = `
.container {
  width: 100%;
  margin: 0 auto;
  padding: 2rem;
  font-family: 'Segoe UI', sans-serif;
  min-height: 100vh;
  box-sizing: border-box;
}

.container h2 {
  margin-bottom: 1rem;
}

table {
  width: 100%;
  border-collapse: collapse;
  background-color: #fff;
  box-shadow: 0 0 10px rgba(0,0,0,0.05);
  border-radius: 10px;
  overflow: hidden;
}

thead {
  background-color: #f5f5f5;
}

th, td {
  text-align: center;
  padding: 12px 16px;
  font-family: inherit;
}

td {
  vertical-align: middle;
  position: relative;
}

tbody tr:nth-child(even) {
  background-color: #f9f9f9;
}

/* Toggle Switch Refactored Styles */
.toggle-switch {
  position: relative;
  width: 60px;
  height: 28px;
  background-color: #ccc;
  border-radius: 14px;
  cursor: pointer;
  margin: 0 auto;
  transition: background-color 0.3s ease;
}

.toggle-switch.on {
  background-color: #4caf50;
}

.toggle-switch.off {
  background-color: #bbb;
}

.toggle-thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 24px;
  height: 24px;
  background: white;
  border-radius: 50%;
  transition: left 0.3s ease;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.5rem;
  font-weight: bold;
  color: #4caf50;
}

.toggle-switch.on .toggle-thumb {
  left: 34px;
}

.toggle-switch.off .toggle-thumb {
  left: 2px;
}


th:nth-child(5),
.td-date,
td:nth-child(4) {
  min-width: 160px;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

@media (max-width: 768px) {
  .container {
    padding: 1rem;
  }

  th, td {
    padding: 8px;
    font-size: 0.85rem;
  }

  .toggle-switch {
    width: 40px;
    height: 22px;
  }

  .toggle-thumb {
    width: 18px;
    height: 18px;
  }
}
`
